import React, { createContext, useCallback, useEffect, useState } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import useApplicationCycle from '@/hooks/useApplicationCycle';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { logError } from '@/utils/errorHandlerUtil';
import { Spinner } from '@/components/common/Spinner';

interface Props {
  applicationCycles: ApplicationCycle[];
  refetchApplicationCycles: () => Promise<void>;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const ApplicationCycleContext = createContext<Props>({
  applicationCycles: [],
  refetchApplicationCycles: async () => {},
});

export const ApplicationCycleProvider = ({ children }: ProviderProps) => {
  const { getApplicationCycles } = useApplicationCycle();
  const hasFetchedRef = React.useRef(false);

  const [fetchedApplicationCycles, setFetchedApplicationCycles] = useState<
    ApplicationCycle[] | undefined
  >(undefined);

  const refetchApplicationCycles = useCallback(async () => {
    try {
      const applicationCycleResults = await getApplicationCycles();
      setFetchedApplicationCycles(applicationCycleResults);
    } catch (error) {
      logError(`Error refetching application cycles.`, error);
    }
  }, [getApplicationCycles]);

  useEffect(() => {
    let isMounted: boolean = true;

    // Fetch application cycles from the server once on mount
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      getApplicationCycles()
        .then((appCycles: ApplicationCycle[]) => {
          if (isMounted) {
            setFetchedApplicationCycles(appCycles);
          }
        })
        .catch((error) => {
          logError(`Error fetching application cycles.`, error);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [getApplicationCycles]);

  const isLoading = fetchedApplicationCycles === undefined;
  const applicationCycles = fetchedApplicationCycles!;

  return (
    <ApplicationCycleContext.Provider
      value={{
        applicationCycles,
        refetchApplicationCycles,
      }}
    >
      {isLoading ? <Spinner /> : children}
    </ApplicationCycleContext.Provider>
  );
};
